import { TitleCasePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from "@angular/core";
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AudienceTargeting, StrategyTactic, TacticType } from "@front/m19-api-client";
import { SegmentEx, StrategyEx, SupportedAudienceMatchType } from "@front/m19-models";
import { SdStrategyService, SegmentService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  ConfirmPopupData,
  IBadgeComponent,
  IButtonDirective,
  IDropdownMenuComponent,
  IDropdownMenuItemDirective,
  IDropdownMenuTriggerDirective,
  IIconComponent,
  ITableTdDirective,
  ITooltipDirective,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  ProductSegmentModalComponent,
  ProductSegmentModalData,
} from "@m19-board/segments/product-segment-modal.component";
import { filter, map, switchMap } from "rxjs";

@Component({
  selector: "[app-sd-strategy-targeting-row]",
  imports: [
    TranslocoDirective,
    TitleCasePipe,
    ITableTdDirective,
    IBadgeComponent,
    IButtonDirective,
    IIconComponent,
    ITooltipDirective,
    IDropdownMenuComponent,
    IDropdownMenuItemDirective,
    IDropdownMenuTriggerDirective,
  ],
  templateUrl: "./sd-strategy-targeting-row.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdStrategyTargetingRowComponent implements OnInit {
  private readonly modalService = inject(ModalService);
  private readonly segmentService = inject(SegmentService);
  private readonly sdStrategyService = inject(SdStrategyService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly titleCasePipe = new TitleCasePipe();

  readonly TacticType = TacticType;
  readonly supportedAudienceMatchType = SupportedAudienceMatchType;

  tactic = input<StrategyTactic>();
  audience = input<AudienceTargeting>();
  defaultTactic = input(false);
  strategy = input.required<StrategyEx>();
  organizationId = input.required<number>();
  isReadOnly = input.required<boolean>();
  isLastTargeting = input<boolean | undefined>(undefined);

  private segmentResource = rxResource({
    params: () => {
      const segmentId = this.tactic()?.segmentId;
      const strategy = this.strategy();
      return segmentId && strategy ? { segmentId, strategy } : undefined;
    },
    stream: ({ params }) =>
      this.segmentService
        .getSegments(params.strategy.accountId, params.strategy.marketplace)
        .pipe(map((segments) => segments.get(params.segmentId!))),
  });
  segment = this.segmentResource.value;

  readonly typeLabelKey = computed(() => {
    if (this.defaultTactic()) return "sd-targeting.product_page";
    if (this.audience()) return "sd-targeting.remarketing";
    if (this.tactic()?.tacticType === TacticType.BLACKLIST) return "sd-targeting.blacklist_product_pages";
    return "sd-targeting.product_page";
  });

  readonly canEditSegment = computed(() => !!this.tactic()?.segmentId);

  readonly isDeleteDisabled = computed(() => {
    if (this.isReadOnly()) return true;
    if (this.tactic()?.tacticType === TacticType.BLACKLIST) return false;
    return !!this.isLastTargeting();
  });

  ngOnInit(): void {
    const segmentId = this.tactic()?.segmentId;
    if (!segmentId) return;

    this.segmentService
      .getSegments(this.strategy().accountId, this.strategy().marketplace)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((segments) => {
        this.segment.set(segments.get(segmentId));
      });
  }

  editSegment() {
    this.modalService.openModal<ProductSegmentModalData, void>(ProductSegmentModalComponent, {
      modalTitle: this.translocoService.translate("segment-manager.edit_segment_tt", [""]),
      data: {
        segment: this.segment(),
        accountId: this.strategy().accountId,
        marketplace: this.strategy().marketplace,
        isReadOnly: this.isReadOnly(),
      },
    });
  }

  onDelete() {
    if (this.isDeleteDisabled()) return;
    if (this.defaultTactic()) {
      this.confirmRemoveAiPowered();
    } else if (this.tactic()) {
      this.confirmRemoveTactic();
    } else if (this.audience()) {
      this.confirmRemoveAudience();
    }
  }

  private confirmRemoveAiPowered() {
    const strategy = this.strategy();
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate(
        "remove-ia-powered-targeting-popup.remove_ai-powered_targeting_from_strategy",
      ),
      closeMode: "none",
      data: {
        message: this.translocoService.translate("remove-ia-powered-targeting-popup.deletion_confirm"),
        confirmLabel: this.translocoService.translate("common.delete"),
        color: "red",
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this.sdStrategyService.updateStrategyAutoAlgoExploration(
            strategy.accountId,
            strategy.marketplace,
            this.organizationId(),
            strategy.strategyId,
            false,
          ),
        ),
      )
      .subscribe({
        next: () =>
          this.toastrService.success(this.translocoService.translate("sd-targeting.ai-powered_targeting_removed")),
        error: (error) => this.toastrService.error(error),
      });
  }

  private confirmRemoveTactic() {
    const strategy = this.strategy();
    const tactic = this.tactic()!;
    const segmentName = this.segment()?.name ?? "";
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("remove-tactic-popup.remove_targeting_from_strategy"),
      closeMode: "none",
      data: {
        message: this.translocoService.translate("remove-tactic-popup.delete_product_page", [segmentName]),
        confirmLabel: this.translocoService.translate("common.delete"),
        color: "red",
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this.sdStrategyService.removeTacticFromStrategy(
            strategy.accountId,
            strategy.marketplace,
            strategy.strategyId,
            tactic.segmentId,
          ),
        ),
      )
      .subscribe({
        next: () => this.toastrService.success(this.translocoService.translate("tactic.tactic_removed")),
        error: (error) =>
          this.toastrService.error(error, this.translocoService.translate("tactic.tactic_removal_failed")),
      });
  }

  private confirmRemoveAudience() {
    const strategy = this.strategy();
    const audience = this.audience()!;
    const ref = this.modalService.openModal<ConfirmPopupData, boolean>(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("remove-audience-popup.remove_audience_targeting_from_strategy"),
      closeMode: "none",
      data: {
        message: this.translocoService.translate("remove-audience-popup.delete_confirm", [
          this.titleCasePipe.transform(String(audience.expressionType)),
          this.supportedAudienceMatchType[audience.matchType!],
          audience.lookback,
        ]),
        confirmLabel: this.translocoService.translate("common.delete"),
        color: "red",
      },
    });

    ref
      .afterClosed()
      .pipe(
        filter((confirmed) => !!confirmed),
        switchMap(() =>
          this.sdStrategyService.removeRemarketingAudienceFromSdStrategy(
            strategy.accountId,
            strategy.marketplace,
            strategy.strategyId,
            audience.audienceTargetId!,
          ),
        ),
      )
      .subscribe({
        next: () => this.toastrService.success(this.translocoService.translate("sd-targeting.audience_removed")),
        error: (error) =>
          this.toastrService.error(error, this.translocoService.translate("sd-targeting.audience_removal_failed")),
      });
  }
}
